import React, { useCallback, useEffect, useRef } from 'react'

type Props = {
    children: React.ReactNode
    onPress?: () => void
    onLongPress?: () => void
    longPressDelayMs?: number
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onPress'>

export const PressDiv: React.FunctionComponent<Props> = ({
    children,
    onPress,
    onLongPress,
    longPressDelayMs = 500,
    onClick,
    onPointerDown,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
    ...rest
}) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const longPressTriggeredRef = useRef(false)

    const clearLongPressTimer = useCallback(() => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }, [])

    useEffect(() => () => clearLongPressTimer(), [clearLongPressTimer])

    const handlePointerDown = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            onPointerDown?.(e)
            if (e.defaultPrevented || !onLongPress || e.button !== 0) {
                return
            }
            longPressTriggeredRef.current = false
            clearLongPressTimer()
            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null
                longPressTriggeredRef.current = true
                onLongPress()
            }, longPressDelayMs)
        },
        [onPointerDown, onLongPress, longPressDelayMs, clearLongPressTimer],
    )

    const endPointerPress = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            clearLongPressTimer()
        },
        [clearLongPressTimer],
    )

    const handlePointerUp = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            onPointerUp?.(e)
            endPointerPress(e)
        },
        [onPointerUp, endPointerPress],
    )

    const handlePointerCancel = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            onPointerCancel?.(e)
            endPointerPress(e)
        },
        [onPointerCancel, endPointerPress],
    )

    const handlePointerLeave = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            onPointerLeave?.(e)
            if (e.buttons === 0) {
                endPointerPress(e)
            }
        },
        [onPointerLeave, endPointerPress],
    )

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            onClick?.(e)
            if (e.defaultPrevented) {
                return
            }
            if (longPressTriggeredRef.current) {
                longPressTriggeredRef.current = false
                return
            }
            onPress?.()
        },
        [onClick, onPress],
    )

    return (
        <div
            {...rest}
            onClick={handleClick}
            onPointerDown={onLongPress ? handlePointerDown : onPointerDown}
            onPointerUp={onLongPress ? handlePointerUp : onPointerUp}
            onPointerCancel={onLongPress ? handlePointerCancel : onPointerCancel}
            onPointerLeave={onLongPress ? handlePointerLeave : onPointerLeave}
        >
            {children}
        </div>
    )
}
